'use client'

/**
 * Synoptic Project Provider
 * Gère les projets synoptiques avec auto-save, cache local et sync entre onglets
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react'
import { useAuth } from './auth-provider'
import { supabase } from './supabaseClient'
import { toast } from 'sonner'
import { useLanguage } from './i18n/language-provider'
import { useSynopticUrl } from '@/hooks/use-synoptic-url'
import { getApiUrl } from './api-url'
import {
  uploadSynopticProjectData,
  downloadSynopticProjectData,
  deleteSynopticProjectData,
  synopticDataPath,
  SynopticStorageError
} from './synoptic-storage'
import { saveSynopticLocalBackup, deleteSynopticLocalBackup, getSynopticLocalBackup } from './synoptic-local-backup'

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

async function withRetry<T>(fn: () => Promise<T>, attempts: number, baseDelayMs: number): Promise<T> {
  let lastError: unknown
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await fn()
    } catch (err) {
      lastError = err
      if (attempt < attempts - 1) await sleep(baseDelayMs * (attempt + 1))
    }
  }
  throw lastError
}

interface SynopticProject {
  id: string
  user_id: string
  name: string
  description?: string
  data: any
  data_path?: string | null
  thumbnail_url?: string
  last_opened_at: string
  created_at: string
  updated_at: string
  version: number
  folder_id?: string | null
  read_only?: boolean // Mode lecture seule pour les admins
  is_admin_view?: boolean // Indique si c'est une vue admin
  profiles?: { // Informations du propriétaire (mode admin)
    email: string
    display_name: string
  }
}

export interface SynopticFolder {
  id: string
  user_id: string
  name: string
  color?: string
  icon?: string
  created_at: string
  updated_at: string
}

interface SynopticProjectContextType {
  // État actuel
  currentProject: SynopticProject | null
  projectData: any | null
  isSaving: boolean
  isSaved: boolean
  lastSavedAt: Date | null
  isLoadingFromUrl: boolean
  loadingProjectName: string | null
  
  // Actions
  loadProject: (id: string) => Promise<void>
  loadProjectFromUrl: () => Promise<void>
  createProject: (name?: string, initialData?: any) => Promise<SynopticProject>
  saveProject: (data: any, options?: { debounce?: boolean }) => Promise<void>
  updateProjectName: (name: string, projectId?: string) => Promise<void>
  duplicateProject: (projectId: string, newName?: string) => Promise<SynopticProject>
  deleteProject: (projectId: string) => Promise<void>
  closeProject: () => void
  
  // Liste de projets
  projects: SynopticProject[]
  loadProjects: (adminMode?: boolean) => Promise<void>
  
  // Dossiers
  folders: SynopticFolder[]
  loadFolders: () => Promise<void>
  createFolder: (name: string) => Promise<SynopticFolder>
  updateFolder: (id: string, name: string) => Promise<void>
  deleteFolder: (id: string) => Promise<void>
  moveProjectToFolder: (projectId: string, folderId: string | null) => Promise<void>
  
  // URL
  projectIdFromUrl: string | null

  // Avertissement de sortie avec modifications non sauvegardées (bouton Retour navigateur)
  exitPromptOpen: boolean
  confirmExitWithoutSaving: () => void
  stayAndSave: () => void

  // Partage
  toggleProjectPublic: (projectId: string, isPublic: boolean) => Promise<{ is_public: boolean; share_token: string | null } | null>
  /** Garantit un report_token (accès lecture seule via QR des rapports PDF, indépendant de is_public). Idempotent. */
  ensureReportToken: (projectId: string) => Promise<string | null>
  
  // Gestion des erreurs
  error: string | null
}

const SynopticProjectContext = createContext<SynopticProjectContextType | undefined>(undefined)

export function SynopticProjectProvider({ children }: { children: ReactNode }) {
  const { user, isPro, session } = useAuth()
  const { t } = useLanguage()

  // Refs de suivi des modifications non sauvegardées (lues par shouldBlockExit au popstate)
  const pendingSaveDataRef = useRef<unknown>(null)
  const isSavingRef = useRef(false)
  const shouldBlockExit = useCallback(
    () => pendingSaveDataRef.current !== null || isSavingRef.current,
    []
  )

  const {
    updateUrlWithProject,
    clearProjectUrl,
    getProjectIdFromUrl,
    projectIdFromUrl,
    isOnHomePage,
    exitPromptOpen,
    confirmExit,
    cancelExit
  } = useSynopticUrl({ shouldBlockExit })
  
  const [currentProject, setCurrentProject] = useState<SynopticProject | null>(null)
  const [projectData, setProjectData] = useState<any | null>(null)
  const [projects, setProjects] = useState<SynopticProject[]>([])
  const [folders, setFolders] = useState<SynopticFolder[]>([])
  
  const [isSaving, setIsSaving] = useState(false)
  const [isSaved, setIsSaved] = useState(true)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoadingFromUrl, setIsLoadingFromUrl] = useState(false)
  const [loadingProjectName, setLoadingProjectName] = useState<string | null>(null)
  
  // Refs pour debouncing
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null)
  
  // Helper pour les appels API avec timeout (évite les blocages du client Supabase)
  const apiCall = useCallback(async (
    url: string,
    options: RequestInit,
    timeoutMs: number = 15000
  ): Promise<any> => {
    // ✅ FIX: Use token from React state (kept fresh by onAuthStateChange TOKEN_REFRESHED)
    // instead of calling supabase.auth.getSession() which can deadlock (auth-js#762)
    const accessToken = session?.access_token
    if (!accessToken) {
      throw new Error('NO_ACCESS_TOKEN')
    }
    
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
    
    try {
      const response = await fetch(getApiUrl(url), {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...options.headers
        },
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('SESSION_EXPIRED')
        }
        const text = await response.text().catch(() => '')
        throw new Error(`API_ERROR_${response.status}: ${text}`)
      }
      
      return response.json()
    } catch (error: any) {
      clearTimeout(timeoutId)
      if (error.name === 'AbortError') {
        throw new Error('TIMEOUT')
      }
      throw error
    }
  }, [session?.access_token])
  
  // Refs pour les fonctions de chargement (utilisées dans les callbacks de reconnexion)
  const loadProjectsRef = useRef<() => Promise<void>>(() => Promise.resolve())
  const loadFoldersRef = useRef<() => Promise<void>>(() => Promise.resolve())
  const setupRealtimeSubscriptionsRef = useRef<() => void>(() => {})
  
  /**
   * Initialiser BroadcastChannel pour sync entre onglets
   */
  useEffect(() => {
    if (typeof window === 'undefined') return
    
    broadcastChannelRef.current = new BroadcastChannel('synoptic-sync')
    
    broadcastChannelRef.current.onmessage = (event) => {
      const { type, projectId, data } = event.data
      
      if (type === 'project-updated' && projectId === currentProject?.id) {
        // Un autre onglet a sauvegardé → rafraîchir
        setProjectData(data)
        setIsSaved(true)
        toast.info(t('synopticProvider.projectUpdatedOtherTab'))
      }
    }
    
    return () => {
      broadcastChannelRef.current?.close()
    }
  }, [currentProject?.id, t])
  
  // Ref pour accéder au projet courant dans les callbacks Realtime sans re-render
  const currentProjectRef = useRef(currentProject)
  useEffect(() => {
    currentProjectRef.current = currentProject
  }, [currentProject])
  
  // Refs pour les channels Realtime (pour pouvoir reconnecter)
  const projectsChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null)
  const foldersChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null)
  const realtimeReconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  
  /**
   * Fonction pour créer/recréer les subscriptions Realtime
   */
  const setupRealtimeSubscriptions = useCallback(() => {
    if (!user?.id || !isPro) return
    
    // Cleanup des anciens channels si existants
    if (projectsChannelRef.current) {
      supabase.removeChannel(projectsChannelRef.current)
    }
    if (foldersChannelRef.current) {
      supabase.removeChannel(foldersChannelRef.current)
    }
    
    // Subscription pour les projets
    projectsChannelRef.current = supabase
      .channel(`synoptic-projects-${user.id}-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'synoptic_projects',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const newProject = payload.new as SynopticProject
            setProjects(prev => {
              if (prev.some(p => p.id === newProject.id)) return prev
              return [{ ...newProject, data: null }, ...prev]
            })
          } else if (payload.eventType === 'UPDATE') {
            const updatedProject = payload.new as SynopticProject
            setProjects(prev => prev.map(p => 
              p.id === updatedProject.id 
                ? { ...updatedProject, data: null }
                : p
            ))
            if (currentProjectRef.current?.id === updatedProject.id) {
              setCurrentProject(prev => prev ? { ...prev, name: updatedProject.name, folder_id: updatedProject.folder_id } : null)
            }
          } else if (payload.eventType === 'DELETE') {
            const deletedId = (payload.old as any).id
            setProjects(prev => prev.filter(p => p.id !== deletedId))
            if (currentProjectRef.current?.id === deletedId) {
              setCurrentProject(null)
              setProjectData(null)
              toast.warning(t('toast.projectDeletedElsewhere'))
            }
          }
        }
      )
      .subscribe((status) => {
        // ✅ Gérer les erreurs de channel et tenter une reconnexion
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // console.warn('⚠️ Realtime projects channel error, will reconnect...')
          scheduleRealtimeReconnect()
        }
      })
    
    // Subscription pour les dossiers
    foldersChannelRef.current = supabase
      .channel(`synoptic-folders-${user.id}-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'synoptic_folders',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const newFolder = payload.new as SynopticFolder
            setFolders(prev => {
              if (prev.some(f => f.id === newFolder.id)) return prev
              return [...prev, newFolder].sort((a, b) => a.name.localeCompare(b.name))
            })
          } else if (payload.eventType === 'UPDATE') {
            const updatedFolder = payload.new as SynopticFolder
            setFolders(prev => prev.map(f => 
              f.id === updatedFolder.id ? updatedFolder : f
            ))
          } else if (payload.eventType === 'DELETE') {
            const deletedId = (payload.old as any).id
            setFolders(prev => prev.filter(f => f.id !== deletedId))
            setProjects(prev => prev.map(p => 
              p.folder_id === deletedId ? { ...p, folder_id: null } : p
            ))
          }
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // console.warn('⚠️ Realtime folders channel error, will reconnect...')
          scheduleRealtimeReconnect()
        }
      })
  }, [user?.id, isPro, t])
  
  // Mettre à jour la ref après définition de setupRealtimeSubscriptions
  useEffect(() => {
    setupRealtimeSubscriptionsRef.current = setupRealtimeSubscriptions
  }, [setupRealtimeSubscriptions])
  
  /**
   * Planifier une reconnexion Realtime avec délai (anti-spam)
   */
  const scheduleRealtimeReconnect = useCallback(() => {
    if (realtimeReconnectTimeoutRef.current) {
      clearTimeout(realtimeReconnectTimeoutRef.current)
    }
    realtimeReconnectTimeoutRef.current = setTimeout(() => {
      // console.log('🔄 Tentative de reconnexion Realtime...')
      setupRealtimeSubscriptionsRef.current()
      // Recharger aussi les données pour rattraper les évènements manqués
      loadProjectsRef.current()
      loadFoldersRef.current()
    }, 3000) // Attendre 3 secondes avant de reconnecter
  }, [])
  
  /**
   * Supabase Realtime : écouter les changements sur les projets et dossiers
   */
  useEffect(() => {
    if (!user || !isPro) return
    
    setupRealtimeSubscriptions()
    
    return () => {
      // console.log('📡 Cleanup Supabase Realtime subscriptions')
      if (realtimeReconnectTimeoutRef.current) {
        clearTimeout(realtimeReconnectTimeoutRef.current)
      }
      if (projectsChannelRef.current) {
        supabase.removeChannel(projectsChannelRef.current)
      }
      if (foldersChannelRef.current) {
        supabase.removeChannel(foldersChannelRef.current)
      }
    }
  }, [user?.id, isPro, setupRealtimeSubscriptions])
  
  /**
   * Reconnexion Realtime quand l'onglet redevient actif
   */
  useEffect(() => {
    if (!user?.id || !isPro) return
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // console.log('👁️ Onglet actif - Vérification et refresh des données...')
        // Recharger les données pour rattraper les évènements manqués
        loadProjectsRef.current()
        loadFoldersRef.current()
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [user?.id, isPro])
  
  /**
   * Charger la liste de projets
   */
  const loadProjects = useCallback(async (adminMode: boolean = false) => {
    if (!user) return
    
    try {
      const endpoint = adminMode 
        ? '/api/synoptic/projects/all?limit=200'
        : '/api/synoptic/projects?limit=200'
      
      const { projects } = await apiCall(endpoint, {
        method: 'GET'
      })

      // Ajouter data: null pour la compatibilité avec l'interface
      setProjects((projects || []).map((p: any) => ({ ...p, data: null })))
    } catch (err: any) {
      // console.error('Error loading projects:', err)
      setError(err.message)
      if (err.message === 'SESSION_EXPIRED' || err.message === 'NO_ACCESS_TOKEN') {
        toast.warning(t('toast.sessionExpired'))
      } else {
        toast.error(t('synopticProvider.errorLoadingProjects'))
      }
    }
  }, [user, t, apiCall])
  
  // Mettre à jour la ref après définition de loadProjects
  useEffect(() => {
    loadProjectsRef.current = loadProjects
  }, [loadProjects])
  
  /**
   * Charger un projet spécifique
   */
  const loadProject = useCallback(async (id: string) => {
    if (!user || !isPro) {
      toast.error(t('synopticProvider.mustBeProToLoad'))
      return
    }
    
    try {
      const { project } = await apiCall(`/api/synoptic/projects/${id}`, {
        method: 'GET'
      })

      // Données dans Supabase Storage (data_path) : fetch direct pour
      // contourner la limite de 6 Mo des réponses Netlify
      let data = project.data
      let usedBackup = false
      if (project.data_path) {
        const accessToken = session?.access_token
        if (!accessToken) throw new Error('NO_ACCESS_TOKEN')
        try {
          data = await downloadSynopticProjectData({ dataPath: project.data_path, accessToken })
        } catch {
          // Le blob Storage est inaccessible pour l'instant
        }
      }

      // Fallback : si les données cloud sont absentes/vides, utiliser le backup local IndexedDB
      if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
        const backup = await getSynopticLocalBackup(id)
        if (backup?.data) {
          data = backup.data
          usedBackup = true
          toast.info(t('synopticProvider.loadedFromLocalBackup'))
        }
      }

      if (!data) throw new Error('NO_DATA')

      setCurrentProject(project)
      setProjectData(data)
      setIsSaved(!usedBackup)
      setLastSavedAt(usedBackup ? new Date() : new Date(project.updated_at))
      
      // Sauvegarder l'ID en localStorage pour réouverture
      localStorage.setItem('lastSynopticProjectId', id)
      
      // Mettre à jour l'URL avec le projet
      updateUrlWithProject({ id: project.id, name: project.name })
      
      // Ne pas afficher de toast ici, c'est géré dans le composant
      // console.log(`✅ Projet "${data.name}" chargé dans le provider`)
    } catch (err: any) {
      // console.error('Error loading project:', err)
      setError(err.message)
      if (err.message === 'SESSION_EXPIRED' || err.message === 'NO_ACCESS_TOKEN') {
        toast.warning(t('toast.sessionExpired'))
      } else {
        toast.error(t('synopticProvider.errorLoadingProject'))
      }
    }
  }, [user, isPro, t, updateUrlWithProject, apiCall, session?.access_token])
  
  /**
   * Créer un nouveau projet
   */
  const createProject = useCallback(async (
    name: string = 'Nouveau Synoptique',
    initialData: any = {}
  ): Promise<SynopticProject> => {
    if (!user || !isPro) {
      throw new Error('Vous devez être Pro pour créer un projet')
    }
    
    try {
      // Utiliser l'API Next.js pour éviter les blocages du client Supabase
      // (métadonnées uniquement : un éventuel blob data part dans Storage,
      // la limite de 6 Mo des fonctions Netlify l'exige)
      const { project } = await apiCall('/api/synoptic/projects', {
        method: 'POST',
        body: JSON.stringify({ name })
      })

      const hasInitialData = initialData && Object.keys(initialData).length > 0
      if (hasInitialData) {
        const accessToken = session?.access_token
        if (!accessToken) throw new Error('NO_ACCESS_TOKEN')
        const dataPath = await uploadSynopticProjectData({
          userId: user.id,
          projectId: project.id,
          accessToken,
          data: initialData,
          timeoutMs: 60000
        })
        await apiCall(`/api/synoptic/projects/${project.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ data: {}, data_path: dataPath })
        })
        project.data_path = dataPath
      }

      setCurrentProject(project)
      setProjectData(hasInitialData ? initialData : project.data)
      setIsSaved(true)
      setLastSavedAt(new Date())
      
      // Ajouter à la liste locale
      setProjects(prev => [project, ...prev])
      
      // Sauvegarder l'ID
      localStorage.setItem('lastSynopticProjectId', project.id)
      
      // Mettre à jour l'URL avec le nouveau projet
      updateUrlWithProject({ id: project.id, name: project.name })
      
      toast.success(t('synopticProvider.projectCreated', { name }))
      
      return project
    } catch (err: any) {
      // console.error('Error creating project:', err)
      setError(err.message)
      if (err.message === 'SESSION_EXPIRED') {
        toast.warning(t('toast.sessionExpired'))
      } else {
        toast.error(t('synopticProvider.errorCreatingProject'))
      }
      throw err
    }
  }, [user, isPro, t, updateUrlWithProject, apiCall, session?.access_token])
  
  /**
   * Sauvegarder le projet actuel
   */
  const saveProject = useCallback(async (
    data: any,
    options: { debounce?: boolean } = { debounce: true }
  ) => {
    if (!currentProject || !user || !isPro) return

    // Marquer les données comme en attente de sauvegarde (pour l'avertissement à la sortie)
    pendingSaveDataRef.current = data

    // Annuler le timeout précédent si debounce activé
    if (options.debounce && saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    const performSave = async (isAutoRetry = false) => {
      isSavingRef.current = true
      setIsSaving(true)
      setIsSaved(false)
      
      try {
        // Préparer les données à sauvegarder (cloud + local)
        const dataToSave = {
          ...data,
          backupProcessors: data.backupProcessors || {}
        }

        // Backup local durable AVANT toute I/O réseau (IndexedDB accepte les
        // gros projets contrairement à localStorage ~5 Mo)
        await saveSynopticLocalBackup(currentProject.id, dataToSave)

        // ✅ FIX: Use token from React state (kept fresh by onAuthStateChange TOKEN_REFRESHED)
        // instead of calling supabase.auth.getSession() which can deadlock (auth-js#762)
        const accessToken = session?.access_token

        if (!accessToken) {
          // console.warn('⚠️ Aucun access_token disponible, sauvegarde locale uniquement (cloud désactivé)')
          toast.warning(t('toast.sessionExpired'))
        } else {
          // 1. Uploader le blob data dans Supabase Storage — contourne la
          //    limite de 6 Mo des fonctions Netlify (fetch REST direct, pas le
          //    client JS potentiellement zombie). Retry x3 avec backoff.
          const dataPath = currentProject.data_path || synopticDataPath(user.id, currentProject.id)
          await withRetry(
            () => uploadSynopticProjectData({
              userId: user.id,
              projectId: currentProject.id,
              accessToken,
              data: dataToSave,
              timeoutMs: 60000
            }),
            3,
            2000
          )

          // 2. PATCH léger : pointeur Storage + purge du jsonb (migration paresseuse)
          const controller = new AbortController()
          const apiTimeout = setTimeout(() => {
            controller.abort()
          }, 30000)

          try {
            const response = await fetch(getApiUrl(`/api/synoptic/projects/${currentProject.id}`), {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
              },
              body: JSON.stringify({ data: {}, data_path: dataPath }),
              signal: controller.signal
            })

            clearTimeout(apiTimeout)

            if (!response.ok) {
              const text = await response.text().catch(() => '')
              // console.error('❌ API sauvegarde projet - status:', response.status, 'body:', text)
              throw new Error(`API_SAVE_ERROR_${response.status}`)
            }
          } catch (error: any) {
            if (error.name === 'AbortError') {
              // console.error('❌ API sauvegarde timeout (30s)')
              throw new Error('TIMEOUT')
            }
            // console.error('❌ Erreur appel API sauvegarde:', error)
            throw error
          }

          // Mémoriser le pointeur Storage dans l'état local
          setCurrentProject(prev => prev ? { ...prev, data_path: dataPath } : null)
        }

        setProjectData(dataToSave)
        setIsSaved(true)
        setLastSavedAt(new Date())
        setError(null)

        // Ne libérer le flag "modifications en attente" que si aucune version
        // plus récente n'a été enregistrée pendant cette sauvegarde
        if (pendingSaveDataRef.current === data) {
          pendingSaveDataRef.current = null
        }
        
        // Notifier les autres onglets
        broadcastChannelRef.current?.postMessage({
          type: 'project-updated',
          projectId: currentProject.id,
          data
        })
        
      } catch (err: any) {
        // console.error('Error saving project:', err)
        setError(err.message)

        // Conserver les données en attente : l'avertissement de sortie reste
        // armé et une éventuelle nouvelle sauvegarde reprendra ces données
        pendingSaveDataRef.current = data

        const is413 = (err instanceof SynopticStorageError && err.status === 413)
          || err.message?.includes('413')
        const isSessionError = err.message === 'SESSION_EXPIRED'
          || err.message === 'NO_ACCESS_TOKEN'
          || err.message === 'API_SAVE_ERROR_401'

        // Relance automatique unique pour les erreurs transitoires (le 413
        // est définitif, inutile de réessayer)
        if (!isAutoRetry && !is413) {
          saveTimeoutRef.current = setTimeout(() => {
            void performSave(true)
          }, 20000)
        }

        if (isSessionError) {
          toast.warning(t('toast.sessionExpired'))
        } else if (is413) {
          toast.error(t('synopticProvider.errorSavingTooLarge'))
        } else if (!isAutoRetry) {
          toast.warning(t('synopticProvider.saveRetryScheduled'))
        } else {
          toast.error(t('synopticProvider.errorSaving'))
        }
      } finally {
        setIsSaving(false)
        isSavingRef.current = false
      }
    }
    
    if (options.debounce) {
      // Sauvegarder après 6 secondes d'inactivité
      saveTimeoutRef.current = setTimeout(performSave, 6000)
    } else {
      // Sauvegarder immédiatement
      await performSave()
    }
  }, [currentProject, user, isPro, t, session])
  
  /**
   * Renommer un projet (par défaut le projet courant, ou un projet spécifique via projectId)
   */
  const updateProjectName = useCallback(async (name: string, projectId?: string) => {
    const targetId = projectId || currentProject?.id
    if (!targetId || !user) return
    
    try {
      // Utiliser l'API Next.js pour éviter les blocages du client Supabase
      await apiCall(`/api/synoptic/projects/${targetId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name })
      })
      
      // Mettre à jour le projet courant si c'est lui qu'on renomme
      if (currentProject?.id === targetId) {
        setCurrentProject(prev => prev ? { ...prev, name } : null)
        updateUrlWithProject({ id: targetId, name })
      }
      
      // Mettre à jour la liste des projets localement (Realtime le fera aussi)
      setProjects(prev => prev.map(p => p.id === targetId ? { ...p, name } : p))
      
      toast.success(t('synopticProvider.projectRenamed'))
    } catch (err: any) {
      // console.error('Error renaming project:', err)
      if (err.message === 'SESSION_EXPIRED') {
        toast.warning(t('toast.sessionExpired'))
      } else {
        toast.error(t('synopticProvider.errorRenaming'))
      }
    }
  }, [currentProject?.id, user, t, updateUrlWithProject, apiCall])
  
  /**
   * Dupliquer un projet
   */
  const duplicateProject = useCallback(async (
    projectId: string,
    newName?: string
  ): Promise<SynopticProject> => {
    if (!user || !isPro) {
      throw new Error('Vous devez être Pro pour dupliquer un projet')
    }
    
    try {
      // Utiliser l'API Next.js pour charger le projet source
      const { project: sourceProject } = await apiCall(`/api/synoptic/projects/${projectId}`, {
        method: 'GET'
      })

      const accessToken = session?.access_token
      if (!accessToken) throw new Error('NO_ACCESS_TOKEN')

      // Données source : dans Storage si le projet a été migré, sinon jsonb
      const sourceData = sourceProject.data_path
        ? await downloadSynopticProjectData({ dataPath: sourceProject.data_path, accessToken })
        : sourceProject.data

      // Créer la copie via l'API (métadonnées uniquement)
      const duplicateName = newName || `${sourceProject.name} (copie)`

      const { project: newProject } = await apiCall('/api/synoptic/projects', {
        method: 'POST',
        body: JSON.stringify({
          name: duplicateName,
          description: sourceProject.description
        })
      })

      // Uploader une copie du blob data sous le nouvel id projet
      if (sourceData && typeof sourceData === 'object' && Object.keys(sourceData).length > 0) {
        const dataPath = await uploadSynopticProjectData({
          userId: user.id,
          projectId: newProject.id,
          accessToken,
          data: sourceData,
          timeoutMs: 60000
        })
        await apiCall(`/api/synoptic/projects/${newProject.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ data: {}, data_path: dataPath })
        })
        newProject.data_path = dataPath
      }
      
      // Ajouter à la liste
      setProjects(prev => [newProject, ...prev])
      
      toast.success(t('synopticProvider.projectDuplicated', { name: duplicateName }))
      
      return newProject
    } catch (err: any) {
      // console.error('Error duplicating project:', err)
      if (err.message === 'SESSION_EXPIRED') {
        toast.warning(t('toast.sessionExpired'))
      } else {
        toast.error(t('synopticProvider.errorDuplicating'))
      }
      throw err
    }
  }, [user, isPro, t, apiCall, session?.access_token])
  
  /**
   * Supprimer un projet
   */
  const deleteProject = useCallback(async (projectId: string) => {
    if (!user) return
    
    try {
      const deletedProject = projects.find(p => p.id === projectId)
        ?? (currentProject?.id === projectId ? currentProject : null)

      // Utiliser l'API Next.js pour éviter les blocages du client Supabase
      await apiCall(`/api/synoptic/projects/${projectId}`, {
        method: 'DELETE'
      })

      // Supprimer le blob Storage associé (best-effort)
      const accessToken = session?.access_token
      if (accessToken) {
        try {
          await deleteSynopticProjectData({
            dataPath: deletedProject?.data_path || synopticDataPath(user.id, projectId),
            accessToken
          })
        } catch {
          // Blob déjà supprimé ou inexistant : non bloquant
        }
      }

      // Retirer de la liste
      setProjects(prev => prev.filter(p => p.id !== projectId))
      
      // Si c'est le projet actuel, le décharger
      if (currentProject?.id === projectId) {
        setCurrentProject(null)
        setProjectData(null)
        localStorage.removeItem('lastSynopticProjectId')
      }
      
      // Supprimer le backup local (IndexedDB + ancienne clé localStorage)
      await deleteSynopticLocalBackup(projectId)
      localStorage.removeItem(`synoptic_backup_${projectId}`)
      
      toast.success(t('synopticProvider.projectDeleted'))
    } catch (err: any) {
      // console.error('Error deleting project:', err)
      if (err.message === 'SESSION_EXPIRED') {
        toast.warning(t('toast.sessionExpired'))
      } else {
        toast.error(t('synopticProvider.errorDeleting'))
      }
    }
  }, [user, currentProject, projects, t, apiCall, session?.access_token])
  
  /**
   * Charger un projet depuis l'URL (ou localStorage en fallback pour Tauri)
   * Ne charge PAS automatiquement si on est sur /synoptique sans slug
   */
  const loadFromUrlInFlightRef = useRef(false)
  const loadProjectFromUrl = useCallback(async () => {
    if (!user || !isPro) return

    // Si on est sur la page d'accueil (/synoptique sans slug), ne pas charger automatiquement
    if (isOnHomePage) {
      return
    }

    // Éviter les chargements en double (effet de montage + effet URL)
    if (loadFromUrlInFlightRef.current) {
      return
    }
    loadFromUrlInFlightRef.current = true

    try {
      // 1. Essayer de récupérer l'ID depuis l'URL
      let projectId = getProjectIdFromUrl()

      // 2. Fallback: récupérer depuis localStorage (pour Tauri refresh uniquement)
      // Seulement si on a une URL avec un slug (pas sur /synoptique)
      if (!projectId) {
        projectId = localStorage.getItem('lastSynopticProjectId')
      }

      // 3. Si on a un ID partiel (8 caractères), chercher le projet correspondant
      if (projectId && projectId.length === 8) {
        try {
          const { projects } = await apiCall('/api/synoptic/projects?limit=200', {
            method: 'GET'
          })

          const normalized = projectId.toLowerCase()
          const matching = (projects || []).find((p: any) => String(p.id).toLowerCase().startsWith(normalized))
          if (matching?.id) {
            projectId = matching.id
            // Récupérer le nom du projet pour l'animation
            setLoadingProjectName(matching.name || 'Projet')
          }
        } catch (err) {
          // console.warn('Erreur recherche projet par ID partiel:', err)
        }
      }

      // 4. Charger le projet si on a un ID valide
      if (projectId && projectId.length > 8) {
        // Activer l'animation de chargement
        setIsLoadingFromUrl(true)

        // Délai pour afficher l'animation (comme dans project-hub)
        await new Promise(resolve => setTimeout(resolve, 800))

        await loadProject(projectId)

        // Désactiver l'animation
        setIsLoadingFromUrl(false)
        setLoadingProjectName(null)
      }
    } finally {
      loadFromUrlInFlightRef.current = false
    }
  }, [user, isPro, getProjectIdFromUrl, loadProject, isOnHomePage, apiCall])
  
  /**
   * Fermer le projet actuel et retourner à l'accueil synoptique
   */
  const closeProject = useCallback(() => {
    // Annuler toute sauvegarde en cours et libérer les modifications en attente
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }
    pendingSaveDataRef.current = null
    isSavingRef.current = false

    setCurrentProject(null)
    setProjectData(null)
    setIsSaved(true)
    setLastSavedAt(null)
    localStorage.removeItem('lastSynopticProjectId')
    clearProjectUrl()
  }, [clearProjectUrl])

  /**
   * Fermer le projet quand l'URL revient sur /synoptique
   * (bouton Retour du navigateur ou navigation directe vers le hub)
   */
  useEffect(() => {
    if (isOnHomePage && currentProjectRef.current) {
      closeProject()
    }
  }, [isOnHomePage, closeProject])

  /**
   * Charger le projet quand l'URL pointe sur un projet alors qu'aucun n'est ouvert
   * (navigation avant, ou arrivée directe sans remount du composant)
   */
  const handledUrlProjectRef = useRef<string | null>(null)
  useEffect(() => {
    if (isOnHomePage) {
      handledUrlProjectRef.current = null
      return
    }
    if (
      projectIdFromUrl &&
      user &&
      isPro &&
      !currentProjectRef.current &&
      handledUrlProjectRef.current !== projectIdFromUrl
    ) {
      handledUrlProjectRef.current = projectIdFromUrl
      loadProjectFromUrl()
    }
  }, [isOnHomePage, projectIdFromUrl, user, isPro, loadProjectFromUrl])

  /**
   * Avertissement natif du navigateur si des modifications sont en cours de
   * sauvegarde lors d'une fermeture d'onglet / navigation hors de l'app
   */
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (pendingSaveDataRef.current !== null || isSavingRef.current) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  /**
   * Confirmer la sortie : abandonner les modifications en attente et revenir en arrière
   */
  const confirmExitWithoutSaving = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }
    pendingSaveDataRef.current = null
    confirmExit()
  }, [confirmExit])

  /**
   * Rester sur le projet et forcer la sauvegarde immédiate des modifications en attente
   */
  const stayAndSave = useCallback(() => {
    const pending = pendingSaveDataRef.current
    if (pending !== null && saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
      void saveProject(pending, { debounce: false })
    }
    cancelExit()
  }, [saveProject, cancelExit])

  /**
   * Charger les dossiers de l'utilisateur
   */
  const loadFolders = useCallback(async () => {
    if (!user || !isPro) return
    
    try {
      const { folders } = await apiCall('/api/synoptic/folders', {
        method: 'GET'
      })

      setFolders(folders || [])
    } catch (err: any) {
      // console.error('Error loading folders:', err)
    }
  }, [user, isPro, apiCall])
  
  // Mettre à jour la ref après définition de loadFolders
  useEffect(() => {
    loadFoldersRef.current = loadFolders
  }, [loadFolders])
  
  /**
   * Créer un nouveau dossier
   */
  const createFolder = useCallback(async (name: string): Promise<SynopticFolder> => {
    if (!user || !isPro) {
      throw new Error('Vous devez être Pro pour créer un dossier')
    }
    
    try {
      // Utiliser l'API Next.js pour éviter les blocages du client Supabase
      const { folder } = await apiCall('/api/synoptic/folders', {
        method: 'POST',
        body: JSON.stringify({ name })
      })
      
      setFolders(prev => [...prev, folder])
      toast.success(t('synopticProvider.folderCreated') || `Dossier "${name}" créé`)
      return folder
    } catch (err: any) {
      // console.error('Error creating folder:', err)
      if (err.message === 'SESSION_EXPIRED') {
        toast.warning(t('toast.sessionExpired'))
      } else {
        toast.error(t('synopticProvider.errorCreatingFolder') || 'Erreur lors de la création du dossier')
      }
      throw err
    }
  }, [user, isPro, t, apiCall])
  
  /**
   * Renommer un dossier
   */
  const updateFolder = useCallback(async (id: string, name: string) => {
    if (!user) return
    
    try {
      // Utiliser l'API Next.js pour éviter les blocages du client Supabase
      await apiCall(`/api/synoptic/folders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name })
      })
      
      setFolders(prev => prev.map(f => f.id === id ? { ...f, name } : f))
      toast.success(t('synopticProvider.folderRenamed') || 'Dossier renommé')
    } catch (err: any) {
      // console.error('Error updating folder:', err)
      if (err.message === 'SESSION_EXPIRED') {
        toast.warning(t('toast.sessionExpired'))
      } else {
        toast.error(t('synopticProvider.errorRenamingFolder') || 'Erreur lors du renommage')
      }
    }
  }, [user, t, apiCall])
  
  /**
   * Supprimer un dossier (les projets sont déplacés à la racine)
   */
  const deleteFolder = useCallback(async (id: string) => {
    if (!user) return
    
    try {
      // Utiliser l'API Next.js pour éviter les blocages du client Supabase
      // L'API gère automatiquement le déplacement des projets à la racine
      await apiCall(`/api/synoptic/folders/${id}`, {
        method: 'DELETE'
      })
      
      setFolders(prev => prev.filter(f => f.id !== id))
      setProjects(prev => prev.map(p => p.folder_id === id ? { ...p, folder_id: null } : p))
      toast.success(t('synopticProvider.folderDeleted') || 'Dossier supprimé')
    } catch (err: any) {
      // console.error('Error deleting folder:', err)
      if (err.message === 'SESSION_EXPIRED') {
        toast.warning(t('toast.sessionExpired'))
      } else {
        toast.error(t('synopticProvider.errorDeletingFolder') || 'Erreur lors de la suppression')
      }
    }
  }, [user, t, apiCall])
  
  /**
   * Déplacer un projet dans un dossier (ou à la racine si folderId = null)
   */
  const moveProjectToFolder = useCallback(async (projectId: string, folderId: string | null) => {
    if (!user) return
    
    try {
      // Utiliser l'API Next.js pour éviter les blocages du client Supabase
      await apiCall(`/api/synoptic/projects/${projectId}`, {
        method: 'PATCH',
        body: JSON.stringify({ folder_id: folderId })
      })
      
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, folder_id: folderId } : p))
      
      const folderName = folderId ? (folders.find(f => f.id === folderId)?.name || 'Folder') : (t('synopticProvider.rootFolder') || 'Root')
      toast.success(t('toast.projectMovedTo', { folder: folderName }))
    } catch (err: any) {
      // console.error('Error moving project:', err)
      if (err.message === 'SESSION_EXPIRED') {
        toast.warning(t('toast.sessionExpired'))
      } else {
        toast.error(t('toast.projectMoveError'))
      }
    }
  }, [user, folders, t, apiCall])
  
  /**
   * Toggle le partage public d'un projet
   */
  const toggleProjectPublic = useCallback(async (
    projectId: string,
    isPublic: boolean
  ): Promise<{ is_public: boolean; share_token: string | null } | null> => {
    if (!user || !isPro) return null
    
    try {
      const { project } = await apiCall(`/api/synoptic/projects/${projectId}/share`, {
        method: 'POST',
        body: JSON.stringify({ is_public: isPublic })
      })
      
      return { is_public: project.is_public, share_token: project.share_token }
    } catch (err: any) {
      if (err.message === 'SESSION_EXPIRED') {
        toast.warning(t('toast.sessionExpired'))
      }
      return null
    }
  }, [user, isPro, t, apiCall])

  /**
   * Garantit l'existence d'un report_token pour le projet — utilisé par les
   * QR codes des rapports PDF pour donner un accès lecture seule même quand
   * le projet reste privé (is_public = false). Idempotent.
   */
  const ensureReportToken = useCallback(async (projectId: string): Promise<string | null> => {
    if (!user || !isPro) return null

    try {
      const { report_token } = await apiCall(`/api/synoptic/projects/${projectId}/report-token`, {
        method: 'POST'
      })
      return typeof report_token === 'string' ? report_token : null
    } catch (err) {
      if (err instanceof Error && err.message === 'SESSION_EXPIRED') {
        toast.warning(t('toast.sessionExpired'))
      }
      return null
    }
  }, [user, isPro, t, apiCall])

  const value: SynopticProjectContextType = {
    currentProject,
    projectData,
    isSaving,
    isSaved,
    lastSavedAt,
    isLoadingFromUrl,
    loadingProjectName,
    loadProject,
    loadProjectFromUrl,
    createProject,
    saveProject,
    updateProjectName,
    duplicateProject,
    deleteProject,
    closeProject,
    projects,
    loadProjects,
    folders,
    loadFolders,
    createFolder,
    updateFolder,
    deleteFolder,
    moveProjectToFolder,
    projectIdFromUrl,
    exitPromptOpen,
    confirmExitWithoutSaving,
    stayAndSave,
    toggleProjectPublic,
    ensureReportToken,
    error
  }
  
  return (
    <SynopticProjectContext.Provider value={value}>
      {children}
    </SynopticProjectContext.Provider>
  )
}

export function useSynopticProject() {
  const context = useContext(SynopticProjectContext)
  if (!context) {
    // En mode développement, on peut vouloir utiliser des valeurs par défaut plutôt que de crash
    // console.warn('useSynopticProject used outside of SynopticProjectProvider')
    // Retourner des valeurs par défaut pour éviter les crashes
    return {
      currentProject: null,
      projectData: null,
      isSaving: false,
      isSaved: true,
      lastSavedAt: null,
      isLoadingFromUrl: false,
      loadingProjectName: null,
      loadProject: async () => {},
      loadProjectFromUrl: async () => {},
      createProject: async () => ({} as SynopticProject),
      saveProject: async () => {},
      updateProjectName: async () => {},
      duplicateProject: async () => ({} as any),
      deleteProject: async () => {},
      closeProject: () => {},
      projects: [],
      loadProjects: async () => {},
      folders: [],
      loadFolders: async () => {},
      createFolder: async () => ({} as any),
      updateFolder: async () => {},
      deleteFolder: async () => {},
      moveProjectToFolder: async () => {},
      projectIdFromUrl: null,
      exitPromptOpen: false,
      confirmExitWithoutSaving: () => {},
      stayAndSave: () => {},
      toggleProjectPublic: async () => null,
      ensureReportToken: async () => null,
      error: null
    }
  }
  return context
}

